import type {
  AcademicTerm,
  AnnualTimetableCell,
  AnnualTimetableSlotKind,
  AnnualWeekdayKey,
  DayKind,
  Grade,
  ProgramFixedContentRow,
  ProgramProgressCell,
  ProgramScopedContentRow,
  Track,
} from '../types/domain';
import type { PageSectionLayout, ClassScheduleSettings } from '../services/sectionLayoutService';
import { loadGradeTrackSectionsCache, loadClassScheduleSettingsCache } from '../services/sectionLayoutService';
import { mapProgressLikeToDisplayFields } from './annualProgressFieldDisplay';
import { type AnnualProjectProgram, matchesAnnualProjectProgramCell } from './annualProjectProgramsStorage';
import { timetableTextForOffDayRow, type OffDayByDate, loadAnnualProgressDataCache } from './annualProgressDataCache';
import { loadProgramProgressDataCache } from './programProgressDataCache';
import { loadProgramProgressCellsCache } from './programProgressCellsCache';
import { loadAnnualProgressScopedTermCache } from './annualProgressScopedTermCache';
import { loadProgramFixedContentRowsCache } from './programFixedContentRowsCache';
import { pickDashboardProgressTerm } from './dashboardTermResolve';
import { mergeTimetableCellsByKeyFromLocalSources } from './timetableCellsByKeyResolve';
import { withAcademicCalendarOffDaysFromLocalCache } from './dashboardAcademicHolidays';
import {
  readAnnualTimetableCellsCacheOrQuery,
  readProgramFixedRowsCacheOrQuery,
  readProgramProgressCellsCacheOrQuery,
  readProgramScopedRowsCacheOrQuery,
} from '../query/readSchoolQueryCache';
import {
  buildGradeTrackTimetableContext,
  normalizeTimetableTextToDefaultAlias,
  resolveTrackNameFromTimetableText,
} from './trackTimetableAlias';
import { addDays, parseISODate, toISODate } from './date';
import {
  buildAnnualCellKey,
  buildAnnualCellMap,
  buildAnnualPeriodLabels,
  deriveAnnualEffectiveCell,
  isAnnualShortDayTailMemo,
  isDedicatedColor,
  isFixedProgramColor,
} from './annualTimetable';
import { buildTrackNameByTimetableTextByGrade, buildWeeksFromRange, resolveClassEntryByGradeId } from './programProgressRows';
import {
  annualSlotHasTimetableProgramAssignment,
  buildScopedRowsAnnualIndex,
  computeScopedOccurrenceForAnnualSlot,
  eventNameFromFixedContentRow,
  pickScopedContentFromAnnualIndex,
  pickScopedRecordRowFromAnnualIndex,
  resolveAnnualSlotProgressSource,
} from './annualProgressSlotSource';
import { isSyncTrackMirrorScope } from './trackSyncScopedContent';
import { loadTrackProgramModeMap } from './trackProgramModeStorage';
import { loadTrackProgramSyncRepMap } from './trackProgramSyncRepStorage';
import {
  buildAnnualSlideWorkspaceScopeId,
  resolveAnnualSlideContentOwnerCandidates,
  resolveAnnualSlideContentSlideOwner,
  resolveAnnualSlideOpenSlideDbOwner,
  resolvePresentationRefIdFromLearningText,
} from './programContentSlideOwner';
import { cellLearningTextForEditor } from './programLearningContentFields';
import { programProgressCellToProgressLike } from './annualProgressFieldDisplay';
import type { ProgramContentSlideOwnerRef } from '../types/programContentSlideCanvas';
import type { ProgramContentSlidePayloadV1 } from './programContentSlideStorage';
import { applyProgramContentSlideLearningOverlayToSlots } from './programContentSlideLearningOverlay';

export type DashboardAnnualDaySlot = {
  slotIndex: number;
  periodLabel: string;
  templateLabel: string;
  timetableText: string;
  timetableColorHex: string;
  timetableSlotKind: AnnualTimetableSlotKind;
  calendarOffDayKind: DayKind | null;
  programKindLabel: string;
  display: ReturnType<typeof mapProgressLikeToDisplayFields>;
  academicYear: number;
  termNumber: 1 | 2;
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey;
  dateIso: string;
  annualCellKey: string;
  progressCellId: string | null;
  recordClassTextRaw: string;
  scopedContentRowId: string | null;
  scopedContentScopeType: string | null;
  scopedContentScopeKey: string | null;
  scopedContentOrderIndex: number | null;
  scopedContentTemplateRowId: string | null;
  fixedContentRowId: string | null;
  fixedContentEventName: string | null;
  fixedContentOrderIndex: number | null;
  /** 연간진도표 '프레젠테이션 열기'와 동일한 슬라이드 파라미터(대시보드 프레젠테이션에서 재사용) */
  slideOwner: ProgramContentSlideOwnerRef | null;
  slideOwnerCandidates: ProgramContentSlideOwnerRef[];
  slideOpenDbOwner: ProgramContentSlideOwnerRef | null;
  slideWorkspaceScopeId: string;
  slideLearningTextRaw: string;
  slideCreatePresentationRefOnSave: ProgramContentSlidePayloadV1['createPresentationRefOnSave'] | undefined;
};

export type DashboardLessonSectionTermData = {
  termNumber: 1 | 2;
  annualRows: AnnualTimetableCell[];
  progressRows: ProgramProgressCell[];
  scopedRows: ProgramScopedContentRow[];
  fixedRows: ProgramFixedContentRow[];
  holidayDays: string[];
  offDayByDate: OffDayByDate;
  /** 학사일정 day_kind — 토요 수업일(class) 반영에 필요 */
  dayKindByDate: Record<string, DayKind>;
};

export type DashboardLessonSectionBundle = {
  academicYear: number;
  terms: AcademicTerm[];
  schedule: ClassScheduleSettings | null;
  tracks: Track[];
  regularColors: string[];
  dedicatedColors: string[];
  baseExcludeColors: string[];
  annualProjectPrograms: AnnualProjectProgram[];
  gradeTrackSections: PageSectionLayout[];
  byTerm: { 1: DashboardLessonSectionTermData; 2: DashboardLessonSectionTermData };
};

type DashboardDaySlotsCoreInput = {
  workspaceId: string;
  gradeId: string;
  grades: Grade[];
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey;
  dateIso: string;
  academicYear: number;
  termNumber: 1 | 2;
  terms: AcademicTerm[];
  schedule: ClassScheduleSettings | null;
  tracks: Track[];
  regularColors: string[];
  dedicatedColors: string[];
  baseExcludeColors: string[];
  holidayDays: string[];
  offDayByDate: OffDayByDate;
  calendarDayKindByDate: Record<string, DayKind>;
  cellsByKey: Record<string, AnnualTimetableCell>;
  programCellsByKey: Record<string, ProgramProgressCell>;
  scopedRows: ProgramScopedContentRow[];
  fixedContentRows: ProgramFixedContentRow[];
  annualProjectPrograms: AnnualProjectProgram[];
  gradeTrackSections: PageSectionLayout[];
};

function buildProgramCellsMapFromRows(rows: ProgramProgressCell[]): Record<string, ProgramProgressCell> {
  const map: Record<string, ProgramProgressCell> = {};
  for (const row of rows) {
    map[buildAnnualCellKey(row.grade_id, row.monday_iso, row.weekday_key, row.slot_index)] = row;
  }
  return map;
}

function buildProgramCellsByKeyFromCache(
  workspaceId: string,
  academicYear: number,
  termNumber: 1 | 2,
  gradeId: string
): Record<string, ProgramProgressCell> {
  const { found, rows } = loadProgramProgressCellsCache(workspaceId, academicYear, termNumber, gradeId);
  if (!found) return {};
  return buildProgramCellsMapFromRows(rows);
}

function buildFixedContentMaps(fixedContentRows: ProgramFixedContentRow[]): {
  fixedContentByEventName: Map<string, ProgramFixedContentRow>;
  fixedContentBySourceAnnualKey: Map<string, ProgramFixedContentRow>;
} {
  const fixedContentByEventName = new Map<string, ProgramFixedContentRow>();
  fixedContentRows.forEach((row) => {
    const key = eventNameFromFixedContentRow(row);
    if (!key || fixedContentByEventName.has(key)) return;
    fixedContentByEventName.set(key, row);
  });
  const fixedContentBySourceAnnualKey = new Map<string, ProgramFixedContentRow>();
  fixedContentRows.forEach((row) => {
    const k = (row.source_annual_cell_key ?? '').trim();
    if (!k || fixedContentBySourceAnnualKey.has(k)) return;
    fixedContentBySourceAnnualKey.set(k, row);
  });
  return { fixedContentByEventName, fixedContentBySourceAnnualKey };
}

function buildProjectProgramByColor(programs: AnnualProjectProgram[], termNumber: 1 | 2): Map<string, AnnualProjectProgram> {
  const map = new Map<string, AnnualProjectProgram>();
  programs.forEach((program) => {
    if (program.termNumber !== termNumber) return;
    const key = program.colorHex.trim().toLowerCase();
    if (!key || map.has(key)) return;
    map.set(key, program);
  });
  return map;
}

function buildDashboardAnnualDaySlotsCore(input: DashboardDaySlotsCoreInput): DashboardAnnualDaySlot[] {
  const {
    workspaceId,
    gradeId,
    grades,
    mondayIso,
    weekdayKey,
    dateIso,
    academicYear,
    termNumber,
    terms,
    schedule,
    tracks,
    regularColors,
    dedicatedColors,
    baseExcludeColors,
    offDayByDate,
    calendarDayKindByDate,
    cellsByKey,
    programCellsByKey,
    scopedRows,
    fixedContentRows,
    annualProjectPrograms,
    gradeTrackSections,
  } = input;

  const holidaySet = new Set(input.holidayDays);
  const termDef = terms.find((t) => t.term_number === termNumber) ?? null;
  const mondayParsed = parseISODate((mondayIso ?? '').trim());
  const safeMonday = !Number.isNaN(mondayParsed.getTime()) ? mondayIso : toISODate(new Date());
  const mondayForAdd = !Number.isNaN(mondayParsed.getTime()) ? mondayParsed : new Date();
  const startsOn = termDef?.starts_on ?? safeMonday;
  const endsOn = termDef?.ends_on ?? toISODate(addDays(mondayForAdd, 6));

  const selectedEntry = resolveClassEntryByGradeId(schedule, grades, gradeId);
  if (!selectedEntry) return [];

  const periodLabels = buildAnnualPeriodLabels(selectedEntry.sectionRows);
  const slotCount = periodLabels.length || 0;
  if (slotCount === 0) return [];

  const scopedRowsIndex = buildScopedRowsAnnualIndex(scopedRows);
  const { fixedContentByEventName, fixedContentBySourceAnnualKey } = buildFixedContentMaps(fixedContentRows);
  const projectProgramByColor = buildProjectProgramByColor(annualProjectPrograms, termNumber);
  const trackNameByTimetableTextByGrade = buildTrackNameByTimetableTextByGrade(tracks);
  const gradeTrackTimetableContext = buildGradeTrackTimetableContext(
    workspaceId,
    gradeId,
    tracks,
    gradeTrackSections
  );
  const trackProgramModeByTrack = loadTrackProgramModeMap(workspaceId, gradeId);
  const trackProgramSyncRepByTrack = loadTrackProgramSyncRepMap(workspaceId, gradeId);
  const weeksForTerm = buildWeeksFromRange(startsOn, endsOn);
  const scopedOccurrenceIndexByAnnualCellKey = new Map<string, number>();
  const scopedOccurrenceCountByScopeKey = new Map<string, number>();

  for (const week of weeksForTerm) {
    const weekdayKeys: AnnualWeekdayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    for (let weekdayIdx = 0; weekdayIdx < weekdayKeys.length; weekdayIdx += 1) {
      const wk = weekdayKeys[weekdayIdx];
      const loopDateIso = week.datesByWeekday[weekdayIdx] ?? '';
      for (let loopSlotIndex = 0; loopSlotIndex < slotCount; loopSlotIndex += 1) {
        const loopStored = cellsByKey[buildAnnualCellKey(gradeId, week.mondayIso, wk, loopSlotIndex)];
        const loopEffective = deriveAnnualEffectiveCell({
          workspaceId,
          academicYear,
          termNumber,
          gradeId,
          mondayIso: week.mondayIso,
          weekdayKey: wk,
          slotIndex: loopSlotIndex,
          dateIso: loopDateIso,
          sectionRows: selectedEntry.sectionRows,
          slotLimitByDay: selectedEntry.slotLimitByDay,
          startsOn,
          endsOn,
          holidaySet,
          storedCell: loopStored,
          dedicatedColors,
          baseExcludeColors,
          calendarDayKindByDate,
        });
        const loopDisplayTextRaw = (loopEffective.textValue ?? '').trim();
        const loopDisplayColor = (loopEffective.colorHex ?? '').trim();
        const loopProgramKindLabel = (() => {
          if (
            matchesAnnualProjectProgramCell(
              loopDisplayColor,
              annualProjectPrograms,
              termNumber,
              loopEffective.isBaseExcluded
            )
          ) {
            return '프로';
          }
          if (isFixedProgramColor(loopDisplayColor, regularColors)) return '고정';
          if (isDedicatedColor(loopDisplayColor, dedicatedColors)) return '전담';
          return '';
        })();
        if (loopProgramKindLabel === '고정') continue;
        const loopProjectForRow =
          loopProgramKindLabel === '프로' ? projectProgramByColor.get(loopDisplayColor.trim().toLowerCase()) ?? null : null;
        /** 아래 칸별 계산(연간진도표와 동일)과 같은 편제명·별칭으로 몇 번째 수업인지 센다 */
        let loopTemplateLabel = loopDisplayTextRaw
          ? trackNameByTimetableTextByGrade[gradeId]?.[loopDisplayTextRaw] ?? ''
          : '';
        if (!loopTemplateLabel && loopDisplayTextRaw && gradeTrackTimetableContext) {
          loopTemplateLabel = resolveTrackNameFromTimetableText(loopDisplayTextRaw, gradeTrackTimetableContext);
        }
        const loopNormalizedDisplayText =
          loopDisplayTextRaw && gradeTrackTimetableContext
            ? normalizeTimetableTextToDefaultAlias(loopDisplayTextRaw, gradeTrackTimetableContext, loopTemplateLabel)
            : loopDisplayTextRaw;
        const loopHasAssignment = annualSlotHasTimetableProgramAssignment(loopDisplayTextRaw, loopDisplayColor);
        let loopPc: ProgramProgressCell | undefined = loopHasAssignment
          ? programCellsByKey[buildAnnualCellKey(gradeId, week.mondayIso, wk, loopSlotIndex)]
          : undefined;
        if (loopPc) {
          const ptt = (loopPc.timetable_text ?? '').trim();
          if (loopDisplayTextRaw && ptt && loopDisplayTextRaw !== ptt) loopPc = undefined;
        }
        const loopScopeLookupAlias = (loopPc?.timetable_text ?? '').trim() || loopNormalizedDisplayText.trim();
        const { scopedOccurrenceKey: loopOccurrenceKey } = computeScopedOccurrenceForAnnualSlot({
          isFixedSlot: false,
          programKindLabel: loopProgramKindLabel,
          projectId: loopProjectForRow?.id ?? null,
          templateLabel: loopTemplateLabel,
          scopeLookupAlias: loopScopeLookupAlias,
          trackProgramModeByTrack,
          trackProgramSyncRepByTrack,
        });
        if (!loopOccurrenceKey) continue;
        const loopAnnualCellKey = buildAnnualCellKey(gradeId, week.mondayIso, wk, loopSlotIndex);
        const occ = scopedOccurrenceCountByScopeKey.get(loopOccurrenceKey) ?? 0;
        scopedOccurrenceIndexByAnnualCellKey.set(loopAnnualCellKey, occ);
        scopedOccurrenceCountByScopeKey.set(loopOccurrenceKey, occ + 1);
      }
    }
  }

  const out: DashboardAnnualDaySlot[] = [];
  for (let slotIndex = 0; slotIndex < slotCount; slotIndex += 1) {
    const row = selectedEntry.sectionRows[slotIndex] ?? {
      kind: 'class' as const,
      name: '',
      startTime: '',
      durationMinutes: '',
      endTime: '',
    };
    const periodLabel = (periodLabelLikeBackup(periodLabels, slotIndex, row, selectedEntry.sectionRows) ?? '').trim() || `${slotIndex + 1}타임`;
    const stored = cellsByKey[buildAnnualCellKey(gradeId, mondayIso, weekdayKey, slotIndex)];
    const effective = deriveAnnualEffectiveCell({
      workspaceId,
      academicYear,
      termNumber,
      gradeId,
      mondayIso,
      weekdayKey,
      slotIndex,
      dateIso,
      sectionRows: selectedEntry.sectionRows,
      slotLimitByDay: selectedEntry.slotLimitByDay,
      startsOn,
      endsOn,
      holidaySet,
      storedCell: stored,
      dedicatedColors,
      baseExcludeColors,
      calendarDayKindByDate,
    });
    const displayTextRaw = (effective.textValue ?? '').trim();
    const displayText =
      effective.slotKind === 'holiday' ? timetableTextForOffDayRow(dateIso, offDayByDate) : displayTextRaw;
    const displayColor = (effective.colorHex ?? '').trim();
    const programKindLabel = (() => {
      if (matchesAnnualProjectProgramCell(displayColor, annualProjectPrograms, termNumber, effective.isBaseExcluded)) {
        return '프로';
      }
      if (isFixedProgramColor(displayColor, regularColors)) return '고정';
      if (isDedicatedColor(displayColor, dedicatedColors)) return '전담';
      return '';
    })();
    const annualCellKey = buildAnnualCellKey(gradeId, mondayIso, weekdayKey, slotIndex);
    const timetableMemoRaw = isAnnualShortDayTailMemo(effective.memo) ? '' : (effective.memo ?? '').trim();
    const tentativeEventKey =
      normalizeFixedEventName(timetableMemoRaw) || normalizeFixedEventName(displayTextRaw);
    const hasTimetableAssignment = annualSlotHasTimetableProgramAssignment(displayTextRaw, displayColor);
    const isFixedSlot = programKindLabel === '고정';
    let pc: ProgramProgressCell | undefined = hasTimetableAssignment ? programCellsByKey[annualCellKey] : undefined;
    if (pc && !isFixedSlot) {
      const dt = displayTextRaw.trim();
      const ptt = (pc.timetable_text ?? '').trim();
      if (dt && ptt && dt !== ptt) pc = undefined;
    }
    const eventKeyForFixed = isFixedSlot ? tentativeEventKey : '';
    const fixedRowMatch = isFixedSlot
      ? fixedContentBySourceAnnualKey.get(annualCellKey.trim()) ??
        (eventKeyForFixed ? fixedContentByEventName.get(eventKeyForFixed) ?? null : null)
      : null;
    const fixedMatchByCell = fixedRowMatch;
    const fixedMatchByEvent = fixedRowMatch;
    let templateLabel = displayTextRaw
      ? trackNameByTimetableTextByGrade[gradeId]?.[displayTextRaw] ?? ''
      : '';
    if (!templateLabel && displayTextRaw && gradeTrackTimetableContext) {
      templateLabel = resolveTrackNameFromTimetableText(displayTextRaw, gradeTrackTimetableContext);
    }
    const normalizedDisplayText =
      displayTextRaw && gradeTrackTimetableContext
        ? normalizeTimetableTextToDefaultAlias(displayTextRaw, gradeTrackTimetableContext, templateLabel)
        : displayTextRaw;
    const rowColorLower = displayColor.trim().toLowerCase();
    const projectForRow = programKindLabel === '프로' ? projectProgramByColor.get(rowColorLower) ?? null : null;
    const scopeLookupAlias = ((pc?.timetable_text ?? '').trim() || normalizedDisplayText.trim());
    const { preferredTrackScopeKey, scopedOccurrenceKey, scopeLookupKey } = computeScopedOccurrenceForAnnualSlot({
      isFixedSlot,
      programKindLabel,
      projectId: projectForRow?.id ?? null,
      templateLabel,
      scopeLookupAlias,
      trackProgramModeByTrack,
      trackProgramSyncRepByTrack,
    });
    const scopedOccurrenceIndex = scopedOccurrenceKey
      ? scopedOccurrenceIndexByAnnualCellKey.get(annualCellKey)
      : undefined;

    const progressSource = resolveAnnualSlotProgressSource({
      scopedRowsIndex,
      annualCellKey,
      programKindLabel,
      isFixedSlot,
      hasTimetableAssignment,
      templateLabel,
      displayText: normalizedDisplayText,
      projectId: projectForRow?.id ?? null,
      legacyPc: pc,
      trackProgramModeByTrack,
      trackProgramSyncRepByTrack,
      scopedOccurrenceIndex,
      preferredTrackScopeKey,
      scopeLookupKey,
      fixedMatchByCell,
      fixedMatchByEvent,
    });
    const d = mapProgressLikeToDisplayFields(progressSource);
    const recordClassTextRaw = progressSource?.record_class_text ?? '';

    const shouldPickScopedRow =
      hasTimetableAssignment && (programKindLabel === '프로' ? !!projectForRow?.id : !!templateLabel.trim());
    const contentScopedRow = shouldPickScopedRow
      ? pickScopedContentFromAnnualIndex(
          scopedRowsIndex,
          annualCellKey,
          programKindLabel,
          templateLabel,
          normalizedDisplayText,
          projectForRow?.id ?? null,
          pc?.timetable_text ?? undefined,
          scopedOccurrenceIndex,
          preferredTrackScopeKey
        )
      : null;
    const recordScopedRow = shouldPickScopedRow
      ? pickScopedRecordRowFromAnnualIndex(
          scopedRowsIndex,
          annualCellKey,
          programKindLabel,
          templateLabel,
          normalizedDisplayText,
          projectForRow?.id ?? null,
          pc?.timetable_text ?? undefined,
          scopedOccurrenceIndex,
          scopeLookupKey,
          preferredTrackScopeKey,
          trackProgramModeByTrack
        )
      : null;
    const isMirror = isSyncTrackMirrorScope(
      templateLabel,
      scopeLookupKey,
      preferredTrackScopeKey,
      trackProgramModeByTrack
    );
    const lessonSaveScopedRow = recordScopedRow ?? contentScopedRow;
    const fixedRow = isFixedSlot ? fixedRowMatch : null;

    // 연간진도표 행 빌드와 동일하게 슬라이드 open 파라미터를 계산한다(scopeId 없으면 편집기 read-only).
    const scopedRowForOwner = contentScopedRow;
    const fixedRowForOwner = fixedRow;
    const slideOwner = resolveAnnualSlideContentSlideOwner({
      isFixedSlot,
      fixedRow: fixedRowForOwner,
      scopedRow: scopedRowForOwner,
      programCell: pc,
    });
    const slideOwnerCandidates = resolveAnnualSlideContentOwnerCandidates({
      isFixedSlot,
      fixedRow: fixedRowForOwner,
      scopedRow: scopedRowForOwner,
      programCell: pc,
    });
    const slideOpenDbOwner = resolveAnnualSlideOpenSlideDbOwner({
      isFixedSlot,
      fixedRow: fixedRowForOwner,
      scopedRow: scopedRowForOwner,
      programCell: pc,
    });
    const slideWorkspaceScopeId = buildAnnualSlideWorkspaceScopeId({
      programKindLabel,
      isFixedSlot,
      workspaceId,
      gradeId,
      academicYear,
      termNumber,
      projectId: projectForRow?.id ?? null,
      trackWorkspaceScopeKey: preferredTrackScopeKey.trim() || scopeLookupKey.trim(),
    });
    const slideLearningTextRaw =
      cellLearningTextForEditor(progressSource) ||
      (pc ? cellLearningTextForEditor(programProgressCellToProgressLike(pc)) : '') ||
      '';
    const progressLikeForSlide =
      progressSource ?? (pc ? programProgressCellToProgressLike(pc) : null);
    const slideSaveMode: 'program_cells' | 'fixed_content' =
      isFixedSlot || shouldPickScopedRow ? 'fixed_content' : 'program_cells';
    const slideProgramMode: 'default' | 'fixed_to_unit' | 'project_to_template' = isFixedSlot
      ? 'fixed_to_unit'
      : programKindLabel === '프로'
        ? 'project_to_template'
        : 'default';
    const slideCreatePresentationRefOnSave: ProgramContentSlidePayloadV1['createPresentationRefOnSave'] | undefined =
      resolvePresentationRefIdFromLearningText(slideLearningTextRaw)
        ? undefined
        : {
            annualKey: annualCellKey,
            learningText: slideLearningTextRaw,
            saveMode: slideSaveMode,
            mode: slideProgramMode,
            gradeId,
            mondayIso,
            weekdayKey,
            slotIndex,
            orderIndex: isFixedSlot
              ? fixedRowForOwner?.order_index ?? slotIndex
              : /** 편제·프로젝트는 일정 위치(몇 번째 수업)가 행 위치 */
                Number.isFinite(scopedOccurrenceIndex)
                ? Number(scopedOccurrenceIndex)
                : scopedRowForOwner?.order_index ?? slotIndex,
            sourceAnnualCellKey: isFixedSlot ? annualCellKey : null,
            timetableText: (pc?.timetable_text ?? '').trim() || displayText,
            unitText: progressLikeForSlide?.unit_text ?? '',
            pageText: progressLikeForSlide?.page_text ?? '',
            assistantPageText: progressLikeForSlide?.assistant_page_text ?? '',
            currentPeriodText: progressLikeForSlide?.current_period_text ?? '',
            totalPeriodText: progressLikeForSlide?.total_period_text ?? '',
            materialText: progressLikeForSlide?.material_text ?? '',
            recordCrossCurricularText: progressLikeForSlide?.record_cross_curricular_text ?? '',
            recordMemoText: progressLikeForSlide?.record_memo_text ?? '',
            recordEvaluationText: progressLikeForSlide?.record_evaluation_text ?? '',
            recordClassText: progressLikeForSlide?.record_class_text ?? '',
          };

    out.push({
      slotIndex,
      periodLabel,
      templateLabel,
      timetableText: displayText,
      timetableColorHex: displayColor,
      timetableSlotKind: effective.slotKind,
      calendarOffDayKind: effective.slotKind === 'holiday' ? (offDayByDate[dateIso]?.kind ?? 'holiday') : null,
      programKindLabel,
      display: d,
      academicYear,
      termNumber,
      mondayIso,
      weekdayKey,
      dateIso,
      annualCellKey,
      progressCellId: pc?.id ?? null,
      recordClassTextRaw,
      scopedContentRowId: lessonSaveScopedRow?.id ?? null,
      scopedContentScopeType:
        lessonSaveScopedRow?.scope_type ??
        contentScopedRow?.scope_type ??
        (shouldPickScopedRow ? (programKindLabel === '프로' ? 'project' : 'track') : null),
      scopedContentScopeKey:
        lessonSaveScopedRow?.scope_key ??
        (isMirror
          ? scopeLookupKey
          : contentScopedRow?.scope_key ??
            (shouldPickScopedRow
              ? programKindLabel === '프로'
                ? projectForRow?.id ?? null
                : preferredTrackScopeKey || null
              : null)),
      /** 같은 스코프 일정 중 몇 번째 수업인지(0부터) — 행이 없을 때 이 위치에 만든다 */
      scopedContentOrderIndex:
        typeof scopedOccurrenceIndex === 'number'
          ? scopedOccurrenceIndex
          : lessonSaveScopedRow?.order_index ?? contentScopedRow?.order_index ?? null,
      scopedContentTemplateRowId: isMirror ? contentScopedRow?.id ?? null : null,
      fixedContentRowId: fixedRow?.id ?? null,
      fixedContentEventName: fixedRow?.event_name ?? null,
      fixedContentOrderIndex: fixedRow?.order_index ?? null,
      slideOwner,
      slideOwnerCandidates,
      slideOpenDbOwner,
      slideWorkspaceScopeId,
      slideLearningTextRaw,
      slideCreatePresentationRefOnSave,
    });
  }
  return applyProgramContentSlideLearningOverlayToSlots(out, workspaceId);
}

/** 주간학습안내·연간진도표와 같이 네트워크에서 받은 in-memory 데이터로 하루 슬롯 구성 */
export function buildDashboardAnnualDaySlotsFromLoadedData(params: {
  workspaceId: string;
  gradeId: string;
  grades: Grade[];
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey;
  dateIso: string;
  bundle: DashboardLessonSectionBundle;
  termNumber: 1 | 2;
}): DashboardAnnualDaySlot[] {
  const { workspaceId, gradeId, grades, mondayIso, weekdayKey, dateIso, bundle, termNumber } = params;
  if (!workspaceId || !gradeId) return [];

  const termData = withAcademicCalendarOffDaysFromLocalCache(bundle.byTerm[termNumber], workspaceId, gradeId);
  return buildDashboardAnnualDaySlotsCore({
    workspaceId,
    gradeId,
    grades,
    mondayIso,
    weekdayKey,
    dateIso,
    academicYear: bundle.academicYear,
    termNumber,
    terms: bundle.terms,
    schedule: bundle.schedule,
    tracks: bundle.tracks,
    regularColors: bundle.regularColors,
    dedicatedColors: bundle.dedicatedColors,
    baseExcludeColors: bundle.baseExcludeColors,
    holidayDays: termData.holidayDays,
    offDayByDate: termData.offDayByDate,
    calendarDayKindByDate: termData.dayKindByDate,
    cellsByKey: buildAnnualCellMap(termData.annualRows),
    programCellsByKey: buildProgramCellsMapFromRows(termData.progressRows),
    scopedRows: termData.scopedRows,
    fixedContentRows: termData.fixedRows,
    annualProjectPrograms: bundle.annualProjectPrograms,
    gradeTrackSections: bundle.gradeTrackSections,
  });
}

function preferRicherRows<T>(bundleRows: T[] | undefined, lsRows: T[]): T[] {
  const b = bundleRows ?? [];
  if (lsRows.length > b.length) return lsRows;
  return b.length > 0 ? b : lsRows;
}

/** 연간진도표 1·2학기 페이지와 동일한 RQ·LS 캐시로 학기 슬롯 소스를 읽는다. */
export function loadDashboardLessonTermDataFromCaches(params: {
  workspaceId: string;
  academicYear: number;
  termNumber: 1 | 2;
  gradeId: string;
}): DashboardLessonSectionTermData {
  const { workspaceId, academicYear, termNumber, gradeId } = params;
  const tt = readAnnualTimetableCellsCacheOrQuery(workspaceId, academicYear, termNumber, gradeId);
  const pp = readProgramProgressCellsCacheOrQuery(workspaceId, academicYear, termNumber, gradeId);
  const sc = readProgramScopedRowsCacheOrQuery(workspaceId, academicYear, termNumber, gradeId);
  const fx = readProgramFixedRowsCacheOrQuery(workspaceId, academicYear, termNumber, gradeId);
  const ap = loadAnnualProgressDataCache(workspaceId, academicYear, termNumber);
  const mergedMap = mergeTimetableCellsByKeyFromLocalSources({
    workspaceId,
    academicYear,
    termNumber,
    gradeIds: [gradeId],
    apCellsByKey: ap.found ? ap.state.cellsByKey : {},
    ppdCellsByKey: {},
  });
  const mergedRows = Object.values(mergedMap);
  return {
    termNumber,
    annualRows: preferRicherRows(tt.found ? tt.rows : [], mergedRows),
    progressRows: pp.found ? pp.rows : [],
    scopedRows: sc.found ? sc.rows : [],
    fixedRows: fx.found ? fx.rows : [],
    holidayDays: ap.found ? ap.state.holidayDays : [],
    offDayByDate: ap.found ? ap.state.offDayByDate ?? {} : {},
    dayKindByDate: {},
  };
}

export function mergeDashboardLessonTermData(
  primary: DashboardLessonSectionTermData,
  cached: DashboardLessonSectionTermData
): DashboardLessonSectionTermData {
  return {
    termNumber: primary.termNumber,
    annualRows: preferRicherRows(primary.annualRows, cached.annualRows),
    progressRows: preferRicherRows(primary.progressRows, cached.progressRows),
    scopedRows: preferRicherRows(primary.scopedRows, cached.scopedRows),
    fixedRows: preferRicherRows(primary.fixedRows, cached.fixedRows),
    holidayDays: primary.holidayDays.length > 0 ? primary.holidayDays : cached.holidayDays,
    offDayByDate:
      Object.keys(primary.offDayByDate ?? {}).length > 0 ? primary.offDayByDate : cached.offDayByDate,
    dayKindByDate:
      Object.keys(primary.dayKindByDate ?? {}).length > 0 ? primary.dayKindByDate : cached.dayKindByDate,
  };
}

/**
 * 위클리용: 메모리 번들 + 연간진도 LS(시간표·진도·scoped)를 합쳐 하루 슬롯 구성.
 * 요일마다 DB를 치지 않으며, 단원·학습내용은 program_progress / scoped 캐시에서 온다.
 */
export function buildDashboardWeeklyDaySlots(params: {
  workspaceId: string;
  gradeId: string;
  grades: Grade[];
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey;
  dateIso: string;
  academicYear: number;
  termNumber: 1 | 2;
  bundle: DashboardLessonSectionBundle | null;
}): DashboardAnnualDaySlot[] {
  const {
    workspaceId,
    gradeId,
    grades,
    mondayIso,
    weekdayKey,
    dateIso,
    academicYear,
    termNumber,
    bundle,
  } = params;
  if (!workspaceId || !gradeId) return [];

  const termFromBundle = bundle?.byTerm?.[termNumber];
  const cachedTerm = loadDashboardLessonTermDataFromCaches({
    workspaceId,
    academicYear,
    termNumber,
    gradeId,
  });
  const ap = loadAnnualProgressDataCache(workspaceId, academicYear, termNumber);

  const annualRows = preferRicherRows(termFromBundle?.annualRows, cachedTerm.annualRows);
  const progressRows = preferRicherRows(termFromBundle?.progressRows, cachedTerm.progressRows);
  const scopedRows = preferRicherRows(termFromBundle?.scopedRows, cachedTerm.scopedRows);
  const fixedRows = preferRicherRows(termFromBundle?.fixedRows, cachedTerm.fixedRows);

  if (annualRows.length === 0 && progressRows.length === 0) {
    return buildDashboardAnnualDaySlots({
      workspaceId,
      gradeId,
      grades,
      mondayIso,
      weekdayKey,
      dateIso,
      programSidebarAcademicYear: academicYear,
    });
  }

  const { holidayDays, offDayByDate, dayKindByDate } = withAcademicCalendarOffDaysFromLocalCache(
    {
      holidayDays:
        (termFromBundle?.holidayDays?.length ?? 0) > 0
          ? termFromBundle!.holidayDays
          : ap.found
            ? ap.state.holidayDays
            : [],
      offDayByDate:
        termFromBundle && Object.keys(termFromBundle.offDayByDate ?? {}).length > 0
          ? termFromBundle.offDayByDate
          : ap.found
            ? ap.state.offDayByDate ?? {}
            : {},
      dayKindByDate: termFromBundle?.dayKindByDate ?? {},
    },
    workspaceId,
    gradeId
  );

  const schedule =
    bundle?.schedule ??
    (ap.found ? ap.state.schedule : null) ??
    loadClassScheduleSettingsCache(workspaceId);
  const tracks =
    (bundle?.tracks?.length ?? 0) > 0
      ? bundle!.tracks
      : ap.found
        ? ap.state.tracks
        : [];
  const terms =
    (bundle?.terms?.length ?? 0) > 0
      ? bundle!.terms
      : ap.found
        ? ap.state.terms
        : [];

  return buildDashboardAnnualDaySlotsCore({
    workspaceId,
    gradeId,
    grades,
    mondayIso,
    weekdayKey,
    dateIso,
    academicYear: bundle?.academicYear ?? academicYear,
    termNumber,
    terms,
    schedule,
    tracks,
    regularColors: bundle?.regularColors?.length
      ? bundle.regularColors
      : ap.found
        ? ap.state.regularColors
        : [],
    dedicatedColors: bundle?.dedicatedColors?.length
      ? bundle.dedicatedColors
      : ap.found
        ? ap.state.dedicatedColors
        : [],
    baseExcludeColors: bundle?.baseExcludeColors?.length
      ? bundle.baseExcludeColors
      : ap.found
        ? ap.state.baseExcludeColors
        : [],
    holidayDays,
    offDayByDate,
    calendarDayKindByDate: dayKindByDate,
    cellsByKey: buildAnnualCellMap(annualRows),
    programCellsByKey: buildProgramCellsMapFromRows(progressRows),
    scopedRows,
    fixedContentRows: fixedRows,
    annualProjectPrograms: bundle?.annualProjectPrograms ?? [],
    gradeTrackSections: bundle?.gradeTrackSections ?? loadGradeTrackSectionsCache(workspaceId) ?? [],
  });
}

/**
 * @deprecated 대시보드는 `buildDashboardAnnualDaySlotsFromLoadedData` 사용. LS-only fallback.
 */
export function buildDashboardAnnualDaySlots(params: {
  workspaceId: string;
  gradeId: string;
  grades: Grade[];
  mondayIso: string;
  weekdayKey: AnnualWeekdayKey;
  dateIso: string;
  programSidebarAcademicYear: number | null;
}): DashboardAnnualDaySlot[] {
  const { workspaceId, gradeId, grades, mondayIso, weekdayKey, dateIso, programSidebarAcademicYear } = params;
  if (!workspaceId || !gradeId) return [];

  const term = pickDashboardProgressTerm(
    workspaceId,
    gradeId,
    mondayIso,
    weekdayKey,
    dateIso,
    programSidebarAcademicYear
  );
  const { academicYear, termNumber } = term;

  const ap = loadAnnualProgressDataCache(workspaceId, academicYear, termNumber);
  const ppd = loadProgramProgressDataCache(workspaceId, academicYear, termNumber, gradeId);

  const schedule =
    ap.state.schedule ?? ppd.state.schedule ?? loadClassScheduleSettingsCache(workspaceId);
  const tracks = ap.state.tracks.length > 0 ? ap.state.tracks : ppd.state.tracks;
  const regularColors = ap.state.regularColors.length > 0 ? ap.state.regularColors : ppd.state.regularColors;
  const dedicatedColors = ap.state.dedicatedColors.length > 0 ? ap.state.dedicatedColors : ppd.state.dedicatedColors;
  const baseExcludeColors = ap.state.baseExcludeColors.length > 0 ? ap.state.baseExcludeColors : ppd.state.baseExcludeColors;
  const offDays = withAcademicCalendarOffDaysFromLocalCache(
    {
      holidayDays: ap.state.holidayDays.length > 0 ? ap.state.holidayDays : ppd.state.holidayDays,
      offDayByDate: ap.state.offDayByDate ?? {},
      dayKindByDate: {} as Record<string, DayKind>,
    },
    workspaceId,
    gradeId
  );
  const cellsByKey = mergeTimetableCellsByKeyFromLocalSources({
    workspaceId,
    academicYear,
    termNumber,
    gradeIds: [gradeId],
    apCellsByKey: ap.state.cellsByKey,
    ppdCellsByKey: ap.found ? {} : ppd.state.cellsByKey,
  });
  const terms = ap.state.terms.length > 0 ? ap.state.terms : ppd.state.terms;
  const scopedCached = loadAnnualProgressScopedTermCache(workspaceId, academicYear, termNumber, gradeId);
  const fixedCached = loadProgramFixedContentRowsCache(workspaceId, academicYear, termNumber, gradeId);

  return buildDashboardAnnualDaySlotsCore({
    workspaceId,
    gradeId,
    grades,
    mondayIso,
    weekdayKey,
    dateIso,
    academicYear,
    termNumber,
    terms,
    schedule,
    tracks,
    regularColors,
    dedicatedColors,
    baseExcludeColors,
    holidayDays: offDays.holidayDays,
    offDayByDate: offDays.offDayByDate,
    calendarDayKindByDate: offDays.dayKindByDate,
    cellsByKey,
    programCellsByKey: buildProgramCellsByKeyFromCache(workspaceId, academicYear, termNumber, gradeId),
    scopedRows: scopedCached.found ? scopedCached.rows : [],
    fixedContentRows: fixedCached.found ? fixedCached.rows : [],
    annualProjectPrograms: [],
    gradeTrackSections: loadGradeTrackSectionsCache(workspaceId) ?? [],
  });
}

function normalizeFixedEventName(raw: string): string {
  return raw.trim().replace(/^◆+/, '').replace(/-+$/, '').trim();
}

function periodLabelLikeBackup(
  periodSlotLabels: string[],
  slotIndex: number,
  row: { kind: string; name?: string },
  allRows: { kind: string; name?: string }[]
): string {
  const a = (periodSlotLabels[slotIndex] ?? '').trim();
  if (a) return a;
  const n = (row.name ?? '').trim();
  if (n) return n;
  if (row.kind === 'class') {
    const k = allRows.slice(0, slotIndex + 1).filter((r) => r.kind === 'class').length;
    return `${k}교시`;
  }
  if (row.kind === 'after_school') {
    const k = allRows.slice(0, slotIndex + 1).filter((r) => r.kind === 'after_school').length;
    return `방과후${k}`;
  }
  if (row.kind === 'activity') return '활동';
  return `${slotIndex + 1}타임`;
}
